import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation } from "wouter";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  ExternalLink,
  FileSpreadsheet,
  Loader2,
  Mail,
  PlusCircle,
  Trash2,
  XCircle,
} from "lucide-react";
import { ColumnMapper } from "@/components/migration/ColumnMapper";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MigrationStepper, type MigrationStepStatus } from "./components/MigrationStepper";
import { StepFooter } from "./components/StepFooter";
import { FileHeaderCard } from "./components/FileHeaderCard";

type BulkStepKey = "upload" | "review";

function SuccessStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: string;
}) {
  return (
    <div className="min-w-[120px] text-left">
      <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={`mt-0.5 font-mono text-xl font-semibold ${tone}`}>
        {value}
      </div>
    </div>
  );
}

function NextStep({
  n,
  title,
  body,
}: {
  n: number;
  title: string;
  body: string;
}) {
  return (
    <div className="rounded-lg border p-4">
      <div className="mb-2 inline-flex h-6 w-6 items-center justify-center rounded-full bg-foreground text-[11.5px] font-semibold text-background">
        {n}
      </div>
      <div className="text-sm font-semibold">{title}</div>
      <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
        {body}
      </div>
    </div>
  );
}

function StepStatusBadge({ status }: { status: MigrationStepStatus }) {
  if (status === "confirmed") {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-200 dark:bg-green-900/30 dark:text-green-300">
        <CheckCircle2 className="mr-1 h-3 w-3" /> Confirmed
      </Badge>
    );
  }
  if (status === "previewing") {
    return (
      <Badge className="bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300">
        <Clock className="mr-1 h-3 w-3" /> Ready to review
      </Badge>
    );
  }
  if (status === "error") {
    return (
      <Badge variant="destructive">
        <AlertCircle className="mr-1 h-3 w-3" /> Error
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      Pending
    </Badge>
  );
}

type AgentResolution =
  | { status: "none" }
  | {
      status: "matched";
      matchedBy: "email" | "name";
      user: { id: string; name: string; email: string | null };
    }
  | { status: "ambiguous"; ambiguousCount: number }
  | { status: "not_found"; tried: { name?: string; email?: string } };

type TeamResolution =
  | { status: "none" }
  | { status: "exists"; name: string; existingId: number }
  | { status: "will_create"; name: string };

type AgentLocationResolution =
  | { status: "none" }
  | { status: "no_producer" }
  | { status: "no_employee_record"; city: string }
  | { status: "will_set"; city: string }
  | {
      status: "will_keep_existing";
      city: string;
      existingLocationName: string;
    };

interface RowResolution {
  agent: AgentResolution;
  team: TeamResolution;
  agentLocation: AgentLocationResolution;
}

interface ParsedClientPreview {
  rowNumber: number;
  name: string;
  status: "active" | "inactive" | "pending";
  entityType: string | null;
  ein: string | null;
  sicCode: number | null;
  website: string | null;
  clientSince: string | null;
  groupNumber: string | null;
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  county: string | null;
  country: string | null;
  contactName: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  teams: string | null;
  locations: string | null;
  agent: string | null;
  agentEmail: string | null;
  agentLocation: string | null;
  emailIsPlaceholder: boolean;
  warnings: string[];
  // Decorated server-side after parsing — what the import will do for this row.
  resolution?: RowResolution;
}

interface InternalField {
  key: string;
  label: string;
  group: string;
  required?: boolean;
}

interface AgencyMember {
  id: string;
  name: string;
  email: string | null;
}

interface UploadResponse {
  batch: { id: number; status: string; fileName?: string };
  headers: string[];
  mapping: Record<string, string | null>;
  suggestedMapping: Record<string, string | null>;
  sampleRows: Record<string, string>[];
  internalFields: InternalField[];
  agencyMembers: AgencyMember[];
  clients: ParsedClientPreview[];
  warnings: Array<{ rowNumber: number; severity: string; code: string; message: string }>;
  summary: { totalRows: number; placeholderEmails: number; warnings: number };
}

interface BatchDetail {
  id: number;
  status: string;
  fileName: string | null;
  totalRows: number | null;
  confirmedCount: number | null;
  rawData: {
    headers?: string[];
    sampleRows?: Record<string, string>[];
    clients?: ParsedClientPreview[];
  } | null;
  columnMapping: Record<string, string | null> | null;
  internalFields: InternalField[];
  agencyMembers?: AgencyMember[];
}

interface ConfirmResponse {
  created: Array<{
    rowNumber: number;
    clientId: number;
    name: string;
    emailIsPlaceholder: boolean;
    notes?: string[];
  }>;
  skipped: Array<{ rowNumber: number; name: string; reason: string }>;
  errors: Array<{ rowNumber: number; name: string; message: string }>;
  createdCount: number;
  skippedCount: number;
  errorCount: number;
}

export default function BulkClientImport() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [, setLocation] = useLocation();

  const [batchId, setBatchId] = useState<number | null>(null);
  const [parsed, setParsed] = useState<ParsedClientPreview[]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mapping, setMapping] = useState<Record<string, string | null>>({});
  const [suggested, setSuggested] = useState<Record<string, string | null>>({});
  const [sampleRows, setSampleRows] = useState<Record<string, string>[]>([]);
  const [internalFields, setInternalFields] = useState<InternalField[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [confirmResult, setConfirmResult] = useState<ConfirmResponse | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeStep, setActiveStep] = useState<BulkStepKey>("upload");
  const [agencyMembers, setAgencyMembers] = useState<AgencyMember[]>([]);
  // Per-row producer overrides: rowNumber → users.id (agency_member).
  // Set by the broker when the CSV-derived agent didn't match. Sent on confirm.
  const [agentOverrides, setAgentOverrides] = useState<Record<number, string>>({});

  // Step statuses derived from import state — feeds the stepper + footer.
  const uploadStepStatus: MigrationStepStatus = batchId ? "confirmed" : "pending";
  const reviewStepStatus: MigrationStepStatus = confirmResult
    ? "confirmed"
    : batchId
      ? "previewing"
      : "pending";

  // Auto-advance to review once a file is parsed (mirrors per-client wizard).
  useEffect(() => {
    if (batchId && parsed.length > 0 && activeStep === "upload" && !confirmResult) {
      setActiveStep("review");
    }
  }, [batchId, parsed.length, confirmResult, activeStep]);

  // Re-fetch batch detail when batchId is set so we get fresh data after PATCH /mapping.
  const batchQuery = useQuery<BatchDetail>({
    queryKey: [`/api/migration/batches/${batchId}`],
    enabled: batchId !== null && confirmResult === null,
  });

  useEffect(() => {
    if (!batchQuery.data) return;
    const d = batchQuery.data;
    const clients = (d.rawData?.clients ?? []) as ParsedClientPreview[];
    setParsed(clients);
    setHeaders(d.rawData?.headers ?? []);
    setMapping(d.columnMapping ?? {});
    setSampleRows(d.rawData?.sampleRows ?? []);
    setInternalFields(d.internalFields ?? []);
    if (d.agencyMembers) setAgencyMembers(d.agencyMembers);
    // On first load (selected empty), select all rows by default.
    setSelected((prev) => {
      if (prev.size > 0) return prev;
      return new Set(clients.map((c) => c.rowNumber));
    });
  }, [batchQuery.data]);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch("/api/migration/batches/upload", {
        method: "POST",
        body: form,
        credentials: "include",
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message ?? `Upload failed (${res.status})`);
      }
      return (await res.json()) as UploadResponse;
    },
    onSuccess: (data) => {
      setBatchId(data.batch.id);
      setHeaders(data.headers);
      setMapping(data.mapping);
      setSuggested(data.suggestedMapping);
      setSampleRows(data.sampleRows);
      setInternalFields(data.internalFields);
      setAgencyMembers(data.agencyMembers ?? []);
      setParsed(data.clients);
      setSelected(new Set(data.clients.map((c) => c.rowNumber)));
      // New file → drop any per-row overrides from a previous batch.
      setAgentOverrides({});
      setConfirmResult(null);
      toast({
        title: "File parsed",
        description: `${data.summary.totalRows} companies found, ${data.summary.placeholderEmails} need a real email.`,
      });
    },
    onError: (err: any) =>
      toast({
        title: "Upload failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  const confirm = useMutation({
    mutationFn: async () => {
      // Only send overrides for rows the broker actually selected.
      const filteredOverrides: Record<number, string> = {};
      for (const [k, v] of Object.entries(agentOverrides)) {
        const rn = Number(k);
        if (selected.has(rn)) filteredOverrides[rn] = v;
      }
      const res = await apiRequest(
        "POST",
        `/api/migration/batches/${batchId}/confirm`,
        {
          selectedRowNumbers: Array.from(selected),
          agentOverrides: filteredOverrides,
        },
      );
      return (await res.json()) as ConfirmResponse;
    },
    onSuccess: (data) => {
      setConfirmResult(data);
      qc.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({
        title: "Import complete",
        description: `${data.createdCount} created, ${data.skippedCount} skipped, ${data.errorCount} errors.`,
      });
    },
    onError: (err: any) =>
      toast({
        title: "Confirm failed",
        description: err?.message ?? "Unknown error",
        variant: "destructive",
      }),
  });

  const deleteBatch = useMutation({
    mutationFn: async () => {
      await apiRequest("DELETE", `/api/migration/batches/${batchId}`);
    },
    onSuccess: () => {
      setBatchId(null);
      setParsed([]);
      setSelected(new Set());
      setConfirmResult(null);
      toast({ title: "Batch discarded" });
    },
  });

  const placeholderCount = useMemo(
    () =>
      parsed.filter((c) => selected.has(c.rowNumber) && c.emailIsPlaceholder)
        .length,
    [parsed, selected],
  );

  const mappingStats = useMemo(() => {
    const mappedHeaders = headers.filter((h) => mapping[h]);
    const ignoredHeaders = headers.filter((h) => !mapping[h]);
    return {
      total: headers.length,
      mapped: mappedHeaders.length,
      ignored: ignoredHeaders,
    };
  }, [headers, mapping]);

  // Preview-table columns: derive from the active mapping so the broker sees
  // every field they mapped, not a hard-coded subset. Name has its own
  // dedicated column (always shown). Status renders a colored badge; email
  // surfaces the placeholder pill; everything else falls back to plain text.
  const previewColumns = useMemo(() => {
    const mappedKeys = new Set(
      Object.values(mapping).filter((v): v is string => v !== null),
    );

    type Col = {
      key: string;
      label: string;
      render: (c: ParsedClientPreview) => ReactNode;
    };
    const all: Col[] = [
      {
        key: "status",
        label: "Status",
        render: (c) => (
          <Badge variant="outline" className="text-xs capitalize">
            {c.status}
          </Badge>
        ),
      },
      {
        key: "entityType",
        label: "Entity",
        render: (c) => c.entityType ?? "—",
      },
      { key: "ein", label: "EIN", render: (c) => c.ein ?? "—" },
      { key: "groupNumber", label: "Group #", render: (c) => c.groupNumber ?? "—" },
      {
        key: "clientSince",
        label: "Date added",
        render: (c) => c.clientSince ?? "—",
      },
      {
        key: "address1",
        label: "Address",
        render: (c) =>
          [c.address1, c.address2].filter(Boolean).join(", ") || "—",
      },
      {
        key: "city",
        label: "City / State",
        render: (c) =>
          [c.city, c.state].filter(Boolean).join(", ") || "—",
      },
      { key: "zipCode", label: "ZIP", render: (c) => c.zipCode ?? "—" },
      { key: "county", label: "County", render: (c) => c.county ?? "—" },
      { key: "country", label: "Country", render: (c) => c.country ?? "—" },
      {
        key: "contactName",
        label: "Contact",
        render: (c) => c.contactName ?? "—",
      },
      {
        key: "contactPhone",
        label: "Phone",
        render: (c) => c.contactPhone ?? "—",
      },
      {
        key: "website",
        label: "Website",
        render: (c) =>
          c.website ? (
            <a
              href={c.website}
              target="_blank"
              rel="noreferrer noopener"
              className="text-blue-600 hover:underline"
            >
              {c.website.replace(/^https?:\/\//, "")}
            </a>
          ) : (
            "—"
          ),
      },
      {
        key: "contactEmail",
        label: "Email",
        render: (c) =>
          c.emailIsPlaceholder ? (
            <Badge
              variant="outline"
              className="border-amber-300 bg-amber-50 text-[10px] text-amber-800"
            >
              <Mail className="mr-1 h-2.5 w-2.5" />
              placeholder
            </Badge>
          ) : (
            <span className="text-muted-foreground">{c.contactEmail}</span>
          ),
      },
      {
        key: "teams",
        label: "Team",
        render: (c) => {
          const t = c.resolution?.team;
          if (!t || t.status === "none") return c.teams ?? "—";
          if (t.status === "exists") {
            return (
              <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
                <CheckCircle2 className="h-3 w-3" />
                {t.name}
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400">
              <PlusCircle className="h-3 w-3" />
              Will create "{t.name}"
            </span>
          );
        },
      },
      {
        key: "locations",
        label: "Locations",
        render: (c) => c.locations ?? "—",
      },
      {
        key: "agent",
        label: "Agent",
        render: (c) => {
          const a = c.resolution?.agent;
          const raw = c.agent;
          const override = agentOverrides[c.rowNumber];
          const overrideMember = override
            ? agencyMembers.find((m) => m.id === override)
            : null;

          // Override takes precedence over CSV resolution.
          if (overrideMember) {
            return (
              <div className="space-y-1">
                <span
                  className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"
                  title="Manually overridden — assign on confirm"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  {overrideMember.name}
                  <Badge
                    variant="outline"
                    className="ml-1 text-[9px] px-1 py-0"
                  >
                    override
                  </Badge>
                </span>
                {overrideMember.email && (
                  <div className="text-[10.5px] text-muted-foreground">
                    {overrideMember.email}
                  </div>
                )}
                <button
                  type="button"
                  className="text-[10px] text-muted-foreground hover:text-foreground underline"
                  onClick={() => {
                    setAgentOverrides((prev) => {
                      const next = { ...prev };
                      delete next[c.rowNumber];
                      return next;
                    });
                  }}
                >
                  clear override
                </button>
              </div>
            );
          }

          if (!a || a.status === "none") return raw ?? "—";
          if (a.status === "matched") {
            return (
              <div>
                <span
                  className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"
                  title={`Matched by ${a.matchedBy}`}
                >
                  <CheckCircle2 className="h-3 w-3" />
                  {a.user.name}
                </span>
                {a.user.email && (
                  <div className="text-[10.5px] text-muted-foreground">
                    {a.user.email}
                  </div>
                )}
              </div>
            );
          }

          // not_found / ambiguous → show pick-a-producer dropdown
          const tone =
            a.status === "ambiguous"
              ? "text-amber-700 dark:text-amber-400"
              : "text-red-700 dark:text-red-400";
          const Icon =
            a.status === "ambiguous" ? AlertTriangle : XCircle;
          const label =
            a.status === "ambiguous"
              ? `${raw} · ambiguous (${a.ambiguousCount})`
              : `${raw ?? c.agentEmail ?? "—"} · not found`;
          return (
            <div className="space-y-1">
              <span className={`inline-flex items-center gap-1 ${tone}`}>
                <Icon className="h-3 w-3" />
                {label}
              </span>
              <Select
                value={override ?? ""}
                onValueChange={(v) => {
                  setAgentOverrides((prev) => ({ ...prev, [c.rowNumber]: v }));
                }}
              >
                <SelectTrigger className="h-7 w-[180px] text-xs">
                  <SelectValue placeholder="Pick agent…" />
                </SelectTrigger>
                <SelectContent>
                  {agencyMembers.length === 0 ? (
                    <SelectItem value="__none__" disabled>
                      No agency members
                    </SelectItem>
                  ) : (
                    agencyMembers.map((m) => (
                      <SelectItem key={m.id} value={m.id} className="text-xs">
                        {m.name}
                        {m.email && (
                          <span className="ml-2 text-muted-foreground">
                            {m.email}
                          </span>
                        )}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          );
        },
      },
      {
        key: "agentEmail",
        label: "Agent email",
        render: (c) => c.agentEmail ?? "—",
      },
      {
        key: "agentLocation",
        label: "Producer location",
        render: (c) => {
          const l = c.resolution?.agentLocation;
          const raw = c.agentLocation;
          const override = agentOverrides[c.rowNumber];

          // Override applied → we'll re-resolve location on confirm (we don't
          // have the override producer's employee/location info on the client).
          if (override && raw) {
            return (
              <span
                className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400"
                title="Producer was overridden — location will be applied on confirm if their profile allows."
              >
                {raw} · pending
              </span>
            );
          }

          if (!l || l.status === "none") return raw ?? "—";
          if (l.status === "no_producer") {
            return (
              <span
                className="text-muted-foreground"
                title="No agent matched — pick one in the Agent column to apply this location."
              >
                {raw} · needs agent
              </span>
            );
          }
          if (l.status === "no_employee_record") {
            return (
              <span
                className="inline-flex items-center gap-1 text-amber-700 dark:text-amber-400"
                title="The matched producer has no employee profile, so we can't store this location."
              >
                <AlertTriangle className="h-3 w-3" />
                {l.city} · no employee profile
              </span>
            );
          }
          if (l.status === "will_set") {
            return (
              <span
                className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400"
                title="Producer's office location will be set to this city on confirm"
              >
                <CheckCircle2 className="h-3 w-3" />
                Will set: {l.city}
              </span>
            );
          }
          return (
            <span
              className="text-muted-foreground"
              title={`Producer already has location "${l.existingLocationName}" — kept (CSV said "${l.city}")`}
            >
              Kept: {l.existingLocationName}
            </span>
          );
        },
      },
    ];
    return all.filter((col) => mappedKeys.has(col.key));
  }, [mapping, agentOverrides, agencyMembers]);

  function toggleRow(rowNumber: number, checked: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowNumber);
      else next.delete(rowNumber);
      return next;
    });
  }

  function toggleAll(checked: boolean) {
    if (checked) setSelected(new Set(parsed.map((c) => c.rowNumber)));
    else setSelected(new Set());
  }

  // ─── Unified wizard layout (mirrors per-client ClientMigration.tsx) ───
  return (
    <div className="container mx-auto max-w-6xl space-y-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLocation("/clients")}
          >
            <ArrowLeft className="mr-1 h-4 w-4" /> Back
          </Button>
          <h1 className="text-2xl font-semibold">Bulk import clients</h1>
        </div>
        {batchId && !confirmResult && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => deleteBatch.mutate()}
            disabled={deleteBatch.isPending}
          >
            <Trash2 className="mr-1 h-4 w-4" /> Discard batch
          </Button>
        )}
      </div>

      {/* File header card — only when a file is uploaded */}
      {batchId && batchQuery.data?.fileName && (
        <FileHeaderCard
          fileName={batchQuery.data.fileName}
          sourcePlatform="ease"
          formatLabel="Broker Companies"
          columnCount={headers.length || null}
          rowCount={parsed.length || null}
          isReplacing={upload.isPending}
          onReplace={(f) => upload.mutate(f)}
        />
      )}

      {/* Column mapping (top-level, above the stepper) — only when uploaded */}
      {batchId && headers.length > 0 && (
        <ColumnMapper
          jobId={batchId}
          headers={headers}
          currentMapping={mapping}
          suggestedMapping={
            Object.keys(suggested).length > 0 ? suggested : mapping
          }
          sampleRows={sampleRows}
          internalFields={internalFields}
          isConfirmed={Boolean(confirmResult)}
          remapUrl={`/api/migration/batches/${batchId}/mapping`}
          invalidateQueryKey={`/api/migration/batches/${batchId}`}
          formatSummary={(d) =>
            `${d.summary?.totalRows ?? 0} companies, ${d.summary?.placeholderEmails ?? 0} need email, ${d.summary?.warnings ?? 0} warnings.`
          }
          defaultOpen
          autoCalculatedHeaders={[
            // "Active/Total Employees" come from the Phase 2 census import.
            // "Locations" in Ease is a count, not a list of names.
            "Active Employees",
            "Total Employees",
            "Locations",
          ]}
        />
      )}

      {/* Stepper */}
      <MigrationStepper<BulkStepKey>
        steps={[
          { key: "upload", label: "1. Upload", status: uploadStepStatus },
          {
            key: "review",
            label: "2. Review & import",
            status: reviewStepStatus,
          },
        ]}
        active={activeStep}
        onChange={setActiveStep}
      />

      {/* ─── Step 1: Upload ─── */}
      {activeStep === "upload" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Step 1 — Upload</CardTitle>
                <CardDescription>
                  Export your book of business from your source platform (Ease,
                  Employee Navigator, …) as CSV. We'll auto-detect the columns
                  and let you review every row before creating clients.
                </CardDescription>
              </div>
              <StepStatusBadge status={uploadStepStatus} />
            </div>
          </CardHeader>
          <CardContent>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) upload.mutate(f);
                e.target.value = "";
              }}
            />
            <div
              onClick={() => !upload.isPending && fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                if (f && !upload.isPending) upload.mutate(f);
              }}
              className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 transition-colors ${
                dragOver
                  ? "border-blue-400 bg-blue-50 dark:bg-blue-900/20"
                  : "border-muted-foreground/20 hover:border-muted-foreground/40 bg-muted/20"
              } ${upload.isPending ? "pointer-events-none opacity-60" : "cursor-pointer"}`}
            >
              {upload.isPending ? (
                <>
                  <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                  <span className="text-sm text-blue-600">Parsing file…</span>
                </>
              ) : (
                <>
                  <FileSpreadsheet className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm font-medium">
                    {batchId
                      ? "Replace the current file"
                      : "Drop your CSV file here or click to browse"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    CSV up to 10MB · expects columns like Company, Status,
                    Address, Phone, …
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Step 2: Review & import ─── */}
      {activeStep === "review" && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Step 2 — Review &amp; import</CardTitle>
                <CardDescription>
                  {confirmResult
                    ? "Import complete — review the summary below."
                    : "Uncheck rows you don't want to import. Existing clients (by name, within your agency) are skipped automatically."}
                </CardDescription>
              </div>
              <StepStatusBadge status={reviewStepStatus} />
            </div>
            {!confirmResult && batchId && placeholderCount > 0 && (
              <div className="mt-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-900/10 dark:text-amber-100">
                <Mail className="mr-1 inline h-3 w-3" />
                <strong>{placeholderCount}</strong> selected client(s) have no
                email in the source file — we'll generate a placeholder you can
                fix later.
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {!batchId ? (
              <p className="rounded-md border border-dashed p-8 text-center text-sm text-muted-foreground">
                Upload a CSV in step 1 first.
              </p>
            ) : confirmResult ? (
              <>
                {/* Hub handoff — replaces the dead-end success screen with a
                    primary CTA into the Migration Hub. The created /
                    skipped / errors lists below stay (broker still needs to
                    see what was just imported). */}
                <div className="flex flex-col items-center gap-3 rounded-lg border bg-card p-8 text-center">
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                    <CheckCircle2 className="h-7 w-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold tracking-tight">
                      {confirmResult.createdCount} client
                      {confirmResult.createdCount === 1 ? "" : "s"} imported
                    </h2>
                    <p className="mx-auto mt-1.5 max-w-md text-sm text-muted-foreground">
                      Their company info is in. Now bring in their employees,
                      plans, and enrollments — one CSV per client.
                    </p>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-4 rounded-lg border bg-muted/40 px-5 py-3">
                    <SuccessStat
                      label="Clients created"
                      value={confirmResult.createdCount}
                      tone="text-emerald-600"
                    />
                    <SuccessStat
                      label="Awaiting census"
                      value={confirmResult.createdCount}
                      tone="text-amber-600"
                    />
                    {confirmResult.skippedCount > 0 && (
                      <SuccessStat
                        label="Skipped (existing)"
                        value={confirmResult.skippedCount}
                        tone="text-muted-foreground"
                      />
                    )}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => setLocation("/dashboard")}
                    >
                      I'll do it later
                    </Button>
                    <Button onClick={() => setLocation("/migration/hub")}>
                      Go to Migration Hub
                      <ArrowLeft className="ml-1 h-4 w-4 rotate-180" />
                    </Button>
                  </div>
                  <p className="text-[11px] text-muted-foreground">
                    You can resume any time from the Dashboard banner.
                  </p>
                </div>

                {/* "What's next" 3-step explainer */}
                <div className="rounded-lg border bg-card p-5">
                  <div className="mb-3">
                    <div className="text-sm font-semibold">What's next</div>
                    <div className="text-xs text-muted-foreground">
                      The Migration Hub gives you one place to track and run
                      each client's data import.
                    </div>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    <NextStep
                      n={1}
                      title="Pick a client"
                      body="From the queue, choose any client to begin."
                    />
                    <NextStep
                      n={2}
                      title="Upload census CSV"
                      body="One enrollment census per client, exported from your source platform."
                    />
                    <NextStep
                      n={3}
                      title="Review & confirm"
                      body="Walk through Census → Plans → Enrollments and commit."
                    />
                  </div>
                </div>

                {/* Existing summary badges — kept under the hub handoff so
                    brokers still see counts at a glance. */}
                <div className="flex flex-wrap gap-3">
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                    {confirmResult.createdCount} created
                  </Badge>
                  {confirmResult.skippedCount > 0 && (
                    <Badge variant="outline">
                      {confirmResult.skippedCount} skipped
                    </Badge>
                  )}
                  {confirmResult.errorCount > 0 && (
                    <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
                      {confirmResult.errorCount} errors
                    </Badge>
                  )}
                </div>

                {confirmResult.created.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">
                      Created clients ({confirmResult.created.length})
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Click a client to view it. Start a per-client migration
                      job from the client's page when you're ready to import
                      their census.
                    </p>
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-16">Row</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead className="w-44">Email</TableHead>
                            <TableHead className="w-28 text-right">
                              Open
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {confirmResult.created.map((c) => (
                            <TableRow key={c.clientId}>
                              <TableCell className="text-xs text-muted-foreground">
                                {c.rowNumber}
                              </TableCell>
                              <TableCell className="font-medium">
                                {c.name}
                                {c.notes && c.notes.length > 0 && (
                                  <ul className="mt-1 list-inside list-disc text-xs font-normal text-amber-700">
                                    {c.notes.map((n, i) => (
                                      <li key={i}>{n}</li>
                                    ))}
                                  </ul>
                                )}
                              </TableCell>
                              <TableCell>
                                {c.emailIsPlaceholder ? (
                                  <Badge
                                    variant="outline"
                                    className="border-amber-300 bg-amber-50 text-xs text-amber-800"
                                  >
                                    <Mail className="mr-1 h-3 w-3" />
                                    placeholder
                                  </Badge>
                                ) : (
                                  <span className="text-xs text-muted-foreground">
                                    from CSV
                                  </span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <Link href={`/clients/${c.clientId}`}>
                                  <Button size="sm" variant="ghost">
                                    <ExternalLink className="h-3.5 w-3.5" />
                                  </Button>
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                {confirmResult.skipped.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium">
                      Skipped ({confirmResult.skipped.length})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {confirmResult.skipped.map((s) => (
                        <li
                          key={s.rowNumber}
                          className="text-muted-foreground"
                        >
                          <span className="font-mono text-xs">
                            #{s.rowNumber}
                          </span>{" "}
                          <span className="font-medium text-foreground">
                            {s.name}
                          </span>{" "}
                          — {s.reason}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {confirmResult.errors.length > 0 && (
                  <div className="space-y-2">
                    <h3 className="text-sm font-medium text-red-700">
                      Errors ({confirmResult.errors.length})
                    </h3>
                    <ul className="space-y-1 text-sm">
                      {confirmResult.errors.map((e) => (
                        <li key={e.rowNumber} className="text-red-700">
                          <span className="font-mono text-xs">
                            #{e.rowNumber}
                          </span>{" "}
                          <span className="font-medium">{e.name}</span> —{" "}
                          {e.message}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {confirmResult.created.filter((c) => c.emailIsPlaceholder)
                  .length > 0 && (
                  <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/10 dark:text-amber-100">
                    <Mail className="mr-1 inline h-4 w-4" />
                    <strong>
                      {confirmResult.created.filter(
                        (c) => c.emailIsPlaceholder,
                      ).length}
                    </strong>{" "}
                    created clients use a placeholder email (
                    <code className="text-xs">
                      migration-…@placeholder.local
                    </code>
                    ). Update each one before sending invites.
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <div className="text-sm">
                    <span className="font-medium">{selected.size}</span> of{" "}
                    {parsed.length} selected
                    {mappingStats.total > 0 && (
                      <span className="ml-3 text-xs text-muted-foreground">
                        · {mappingStats.mapped} of {mappingStats.total} columns
                        mapped
                      </span>
                    )}
                  </div>
                  <Button
                    onClick={() => confirm.mutate()}
                    disabled={selected.size === 0 || confirm.isPending}
                  >
                    {confirm.isPending && (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    )}
                    Import {selected.size} client
                    {selected.size === 1 ? "" : "s"}
                  </Button>
                </div>
                <div className="overflow-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10">
                          <Checkbox
                            checked={
                              parsed.length > 0 &&
                              selected.size === parsed.length
                            }
                            onCheckedChange={(v) => toggleAll(Boolean(v))}
                          />
                        </TableHead>
                        <TableHead className="w-12">Row</TableHead>
                        <TableHead>Name</TableHead>
                        {previewColumns.map((col) => (
                          <TableHead key={col.key}>{col.label}</TableHead>
                        ))}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {parsed.map((c) => {
                        const isSel = selected.has(c.rowNumber);
                        return (
                          <TableRow
                            key={c.rowNumber}
                            className={isSel ? "" : "opacity-50"}
                          >
                            <TableCell>
                              <Checkbox
                                checked={isSel}
                                onCheckedChange={(v) =>
                                  toggleRow(c.rowNumber, Boolean(v))
                                }
                              />
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {c.rowNumber}
                            </TableCell>
                            <TableCell className="font-medium">
                              {c.name}
                            </TableCell>
                            {previewColumns.map((col) => (
                              <TableCell
                                key={col.key}
                                className="text-xs"
                              >
                                {col.render(c)}
                              </TableCell>
                            ))}
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Footer */}
      <StepFooter<BulkStepKey>
        currentStep={activeStep}
        steps={[
          { key: "upload", status: uploadStepStatus },
          { key: "review", status: reviewStepStatus },
        ]}
        onBack={() => {
          if (activeStep === "review") setActiveStep("upload");
        }}
        onContinue={() => {
          if (
            activeStep === "upload" &&
            uploadStepStatus === "confirmed"
          ) {
            setActiveStep("review");
          }
        }}
        lastStepCompleteLabel="Import complete"
        lastStepPendingLabel="Confirm import to finish"
      />
    </div>
  );
}
